import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress
} from '@mui/material';
import {
  Check as CheckIcon,
  Payment as PaymentIcon,
  AccountBalanceWallet as WalletIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { connectToStripe } from '../../../../services/stripeService';

interface StripeConnectStepProps {
  data: any;
  onChange: (data: any) => void;
}

const StripeConnectStep: React.FC<StripeConnectStepProps> = ({
  data,
  onChange
}) => {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleStripeConnect = async () => {
    setConnecting(true);
    setError(null);
    
    try {
      // In a real implementation, this would redirect to Stripe Connect
      // and handle the OAuth callback to get the accountId
      const result = await connectToStripe(data.businessName, data.email);
      
      onChange({
        stripeConnected: true,
        stripeAccountId: result.accountId,
      });
    } catch (err: any) {
      console.error('Error connecting to Stripe:', err);
      setError(err.message || 'Failed to connect with Stripe. Please try again.');
    } finally {
      setConnecting(false);
    }
  };
  
  const handleSkip = () => {
    // Mark as skipped but user will need to connect later
    onChange({
      stripeSetupSkipped: true
    });
  };
  
  const benefits = [
    {
      icon: <PaymentIcon />,
      title: 'Accept Payments',
      description: 'Process credit cards, Apple Pay, and Google Pay from customers worldwide.'
    },
    {
      icon: <WalletIcon />,
      title: 'Fast Payouts',
      description: 'Receive funds directly to your bank account in 2-3 business days.'
    },
    {
      icon: <ScheduleIcon />,
      title: 'Automated Reconciliation',
      description: 'We handle payment processing and automatically reconcile all transactions.'
    },
    {
      icon: <SecurityIcon />,
      title: 'Secure & Compliant',
      description: 'PCI compliant payment processing with fraud protection.'
    }
  ];
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payment Processing Setup
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Set up payment processing to accept bookings and receive payments from customers. 
        We use Stripe Connect to securely process payments and transfer funds to your account.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              Benefits of connecting with Stripe
            </Typography>
            
            <List>
              {benefits.map((benefit, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemIcon>
                    {benefit.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={benefit.title}
                    secondary={benefit.description}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {data.stripeConnected ? (
              <>
                <Box>
                  <Alert icon={<CheckIcon />} severity="success" sx={{ mb: 2 }}>
                    Your Stripe account is connected!
                  </Alert>
                  <Typography variant="body2" paragraph>
                    Your account is ready to receive payments. You can manage your payment settings in the venue dashboard after completing setup.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stripe Account ID: {data.stripeAccountId}
                  </Typography>
                </Box>
                
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => onChange({ stripeConnected: false, stripeAccountId: '' })}
                  sx={{ alignSelf: 'flex-start', mt: 2 }}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Connect with Stripe
                  </Typography>
                  <Typography variant="body2" paragraph>
                    We partner with Stripe to transfer payments directly to your bank account. Stripe's fee is 2.9% + 30¢ per transaction, and our platform fee is {data.commissionRate}%.
                  </Typography>
                  
                  <Typography variant="body2" paragraph>
                    By connecting, you agree to Stripe's <Link href="https://stripe.com/connect-account/legal" target="_blank">Connected Account Agreement</Link>.
                  </Typography>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleStripeConnect}
                    disabled={connecting}
                    startIcon={connecting ? <CircularProgress size={20} /> : null}
                    sx={{ mb: 2 }}
                  >
                    {connecting ? 'Connecting...' : 'Connect with Stripe'}
                  </Button>
                  
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={handleSkip}
                    disabled={connecting}
                  >
                    Set up later
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Note: You can still create your venue account without connecting to Stripe, but you won't be able to accept payments until you complete this step.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StripeConnectStep;
